import { ReactNode } from 'react';
import { withStyles, Icon, MaterialSymbol } from 'vopay';
import styles from './TransactionStatus.module.scss';

// Feature-local to ClientAccountList (ID-13350): moved verbatim from client-dashboard's UI/TransactionStatus,
// deps rewritten to vopay only. Not exported from the package — internal to this feature. Promote to
// component-library if a second consumer appears.
// The statuses the component styles/icons explicitly. Callers may still pass any other string (it falls
// back to the neutral gray/pending render), so we widen with `string & {}` — that keeps the known values
// as editor autocomplete instead of collapsing the whole union to `string`.
type KnownTransactionStatus =
  | 'requested'
  | 'pending'
  | 'completed'
  | 'failed'
  | 'in progress'
  | 'sent'
  | 'upcoming'
  | 'successful'
  | 'complete'
  | 'settled'
  | 'cancelled'
  | 'cancellation requested'
  | 'pending Approval'
  | 'waiting'
  | 'active'
  | 'not active'
  | 'neutral'
  | 'positive'
  | 'negative'
  | 'info'
  | 'partially paid'
  | 'open'
  | 'reversed'
  | 'blocked'
  | 'rejected'
  | 'approved';

type TransactionStatusProps = {
  status: KnownTransactionStatus | (string & {});
  label: string;
};

const classes = withStyles(styles);

export default function TransactionStatus({ status, label }: TransactionStatusProps) {
  const getStatusIcon = (): ReactNode => {
    const statusIcons: { [key: string]: ReactNode } = {
      requested: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      neutral: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      info: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      pending: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      failed: <MaterialSymbol icon="cancel" size="18px" color="#e8160b" />,
      'in progress': <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      sent: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      upcoming: <MaterialSymbol icon="calendar_today" size="18px" />,
      successful: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      complete: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      settled: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      active: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      positive: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      negative: <MaterialSymbol icon="cancel" size="18px" color="#e8160b" />,
      cancelled: <MaterialSymbol icon="cancel" size="18px" color="#e8160b" />,
      blocked: <MaterialSymbol icon="block" size="18px" color="#2D2D2DBF" />,
      'cancellation requested': <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      'pending Approval': (
        <div>
          <Icon icon="transactionPending" height="18px" width="18px" color="none" />
        </div>
      ),
      waiting: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      'not active': <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      open: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      'partially paid': <Icon icon="partiallyPaid" height="18px" width="18px" color="none" />,
      reversed: <MaterialSymbol icon="undo" size="18px" color="#e8160b" />,
      rejected: <MaterialSymbol icon="cancel" size="18px" color="#e8160b" />,
      approved: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
    };

    return statusIcons[status] || <Icon icon="transactionPending" height="18px" width="18px" color="none" />;
  };

  const getStatusColor = (): string => {
    const statusColors: { [key: string]: string } = {
      requested: 'gray',
      neutral: 'gray',
      info: 'gray',
      pending: 'gray',
      failed: 'red',
      'in progress': 'gray',
      sent: 'gray',
      upcoming: 'blue',
      successful: 'green',
      complete: 'green',
      settled: 'green',
      active: 'green',
      positive: 'green',
      negative: 'red',
      'not active': 'gray',
      cancelled: 'red',
      'cancellation requested': 'gray',
      'pending Approval': 'gray',
      waiting: 'gray',
      'partially paid': 'gray',
      open: 'gray',
      reversed: 'red',
      blocked: 'gray',
      rejected: 'red',
      approved: 'green',
    };

    return statusColors[status] || 'gray';
  };

  return (
    <div className={classes('transactionStatus', getStatusColor())}>
      {getStatusIcon()}
      <div className={classes('statusLabel')}>{label}</div>
    </div>
  );
}
